/**
 * The live conversation: transcript above, composer pinned below.
 *
 * The composer is mounted unconditionally rather than only once a conversation exists —
 * an empty panel with no input looks broken, and the empty state is a prompt to type,
 * not a separate screen with its own affordances.
 */
import React from 'react'

import { Stack, Typography } from '@mui/material'

import { useAskStore } from 'ask'

import { AskComposer } from './AskComposer'
import { AskMessageList } from './AskMessageList'
import { AskSuggestedQuestions } from './AskSuggestedQuestions'

export const AskConversationView = () => {
  const conversations = useAskStore(s => s.conversations)
  const activeId = useAskStore(s => s.activeId)

  const active = conversations.find(c => c.id === activeId)

  return (
    <>
      {/* Narrowed inline rather than through a `hasMessages` boolean: narrowing via an
          aliased condition only landed in TS 5.5, and the repo's pinned compiler is 4.9.5. */}
      {active && active.messages.length > 0 ? (
        <AskMessageList conversation={active} />
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={3}
          sx={{
            flex: 1,
            px: 6,
            py: 8,
          }}
        >
          {/*
            NO V MARK here — deliberately removed (VRG-227). It was the THIRD V in the
            viewport: the sidebar logo, the header launcher, and this. A brand mark
            repeated three times stops reading as identity and starts reading as
            wallpaper — and it actively undercut the launcher's own argument (see
            AskVeraLauncher), which is that a BARE V means "this is Vera" while a BOXED
            one means "press me". A third bare V, sitting inside the very surface the
            boxed one opens, collapses that distinction at the point it matters most.

            The heading STAYS, and the objection to it did not survive measurement —
            recorded because the wrong version is the intuitive one. `AskPanel` does
            render "Ask Vera" as this panel's header title too, and that was argued as a
            duplication worth deleting on the estimate that the two sat ~40px apart in
            one narrow column. Measured in the browser they are **240px apart** (y=20 and
            y=260) at **different sizes** (20px/600 title, 16px/600 heading). At that
            separation and contrast they do not read as one thing said twice; they read
            as what they are — small persistent chrome identifying the docked panel, and
            the empty state's focal heading framing the question list beneath it. The
            40px figure was eyeballed off a screenshot and was wrong by 6x.

            Without any heading the suggestions float with nothing saying what they are,
            which is the actual cost of removing it.

            Worth knowing but NOT done: a heading naming what FOLLOWS rather than
            repeating the surface — "Try asking" — would describe the list AND retire the
            duplication question entirely. Left as an option, not a pending fix.
          */}
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ fontWeight: 600 }}
          >
            Ask Vera
          </Typography>
          <AskSuggestedQuestions />
        </Stack>
      )}
      <AskComposer />
    </>
  )
}
